import React from "react";
import {
  FaUsers,
  FaRocket,
  FaGraduationCap,
  FaHome,
  FaChalkboardTeacher,
  FaMoneyBillWave,
  FaMapMarkerAlt,
  FaBriefcase,
  FaClock,
  FaArrowRight,
  FaCheckCircle,
} from "react-icons/fa";
import Seo from "../components/Seo/Seo";
import CardsSection from "../components/CardsSection/CardsSection";
import useReveal from "../hooks/useReveal";
import "./Career.css";

const criteria = [
  "Hervorragende Teamfähigkeit und Kommunikationsstärke",
  "Hohe Lernbereitschaft und Eigeninitiative",
  "Fundierte Kenntnisse in HTML, CSS, JavaScript und modernen Frameworks",
  "Kreativer und lösungsorientierter Denkansatz",
  "Selbstständige Arbeitsweise und hohes Verantwortungsbewusstsein",
  "Erste praktische Erfahrungen im Projektmanagement von Vorteil",
];

const offerings = [
  {
    icon: <FaRocket />,
    title: "Praxisnahe Projekte",
    description: "Praxisnahe Erfahrungen in spannenden, anspruchsvollen Kundenprojekten.",
  },
  {
    icon: <FaChalkboardTeacher />,
    title: "Mentoring",
    description: "Mentoring durch erfahrene Experten mit langjähriger Branchenerfahrung.",
  },
  {
    icon: <FaHome />,
    title: "Flexibles Arbeiten",
    description: "Hybride Arbeitsmodelle sowie moderne, kreative Arbeitsumgebungen.",
  },
  {
    icon: <FaGraduationCap />,
    title: "Weiterbildung",
    description: "Umfangreiche Weiterbildungsangebote und regelmäßige Workshops.",
  },
  {
    icon: <FaMoneyBillWave />,
    title: "Attraktive Vergütung",
    description: "Faire Vergütung und langfristige Entwicklungsperspektiven im Team.",
  },
  {
    icon: <FaUsers />,
    title: "Starkes Team",
    description: "Ein eingespieltes, kollegiales Team mit flachen Hierarchien.",
  },
];

const positions = [
  {
    title: "Praktikum – Webdesigner (m/w/d)",
    location: "Hybrid / Bonn",
    type: "Praktikum",
    duration: "mind. 6 Monate",
    description:
      "Du unterstützt unser Team bei der Gestaltung und Umsetzung von Webseiten und erhältst umfassende Einblicke in den gesamten Designprozess.",
  },
  {
    title: "Junior – Webdesigner (m/w/d)",
    location: "Hybrid / Bonn",
    type: "Junior Developer",
    duration: "Vollzeit",
    description:
      "Du wirkst an anspruchsvollen Webprojekten mit, fokussierst Dich auf responsives Design und technische Umsetzung und arbeitest im kreativen Team.",
  },
  {
    title: "Junior – Front End Developer (m/w/d)",
    location: "Hybrid / Bonn",
    type: "Junior Developer",
    duration: "Vollzeit",
    description:
      "Du entwickelst und optimierst moderne Benutzeroberflächen, setzt innovative Features um und arbeitest eng mit unserem Entwicklungsteam zusammen.",
  },
  {
    title: "Senior – Full Stack Developer (m/w/d)",
    location: "Hybrid / Bonn",
    type: "Senior Developer",
    duration: "Vollzeit",
    description:
      "Du entwickelst skalierbare Webanwendungen mit modernen Technologien wie React und Node.js, gestaltest die technische Architektur und unterstützt das Team durch Code Reviews und Mentoring in einem agilen Umfeld.",
  },
];

const CareerPage = () => {
  const introRef = useReveal();
  const criteriaRef = useReveal();
  const positionsRef = useReveal({ threshold: 0.05 });
  const ctaRef = useReveal();

  return (
    <div className="career-page">
      <Seo
        title="Karriere bei Nexite IT – Jobs in Webentwicklung & Design"
        description="Starte deine Karriere bei Nexite IT im Raum Köln/Bonn. Wir bieten spannende Jobs in Webdesign, Frontend- und Fullstack-Entwicklung – mit flexiblem Arbeiten, Weiterbildung & mehr."
        path="/karriere"
      />

      <header className="career-hero">
        <div className="career-hero-inner">
          <span className="career-kicker">Karriere bei Nexite</span>
          <h1>Gestalte mit uns die digitale Zukunft</h1>
          <p>
            Wir suchen Talente im Raum Köln/Bonn, die mit Leidenschaft und
            Kreativität digitale Lösungen bauen wollen, die den Markt
            nachhaltig prägen.
          </p>
          <a href="#offene-positionen" className="career-hero-cta">
            Offene Positionen ansehen <FaArrowRight />
          </a>
        </div>
      </header>

      <section className="career-intro reveal" ref={introRef}>
        <div className="career-intro-inner">
          <h2>Über uns</h2>
          <p>
            Wir sind ein innovatives Unternehmen, gegründet von erfahrenen
            Freiberuflern im Raum Köln/Bonn. Mit Leidenschaft und Kreativität
            entwickeln wir digitale Lösungen, die den Markt nachhaltig
            prägen. Unser Fokus liegt auf der Umsetzung anspruchsvoller
            Projekte, die agile und nachhaltige Ergebnisse liefern.
          </p>
          <p>
            In unserem modernen Arbeitsumfeld fördern wir Eigeninitiative,
            bieten flexible Arbeitszeiten und vielfältige
            Weiterbildungsmöglichkeiten. Wir suchen engagierte Fachkräfte,
            die gemeinsam mit uns die digitale Zukunft gestalten.
          </p>
        </div>
      </section>

      <section className="career-criteria reveal" ref={criteriaRef}>
        <div className="career-criteria-inner">
          <div className="career-criteria-copy">
            <span className="career-kicker">Profil</span>
            <h2>Wen wir suchen</h2>
            <p>
              Du musst nicht alles perfekt können – aber diese Eigenschaften
              solltest du mitbringen, damit wir gemeinsam wachsen können.
            </p>
          </div>
          <ul className="career-criteria-list">
            {criteria.map((item) => (
              <li key={item}>
                <FaCheckCircle />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CardsSection
        title="Was wir bieten"
        items={offerings}
        sectionClass="career-offerings-section"
      />

      <section
        id="offene-positionen"
        className="career-positions reveal"
        ref={positionsRef}
      >
        <div className="career-positions-inner">
          <h2>Offene Positionen</h2>
          <div className="career-positions-grid">
            {positions.map((position, idx) => (
              <article
                key={position.title}
                className="career-position-card"
                style={{ "--card-delay": `${idx * 60}ms` }}
              >
                <div className="career-position-badge">{position.type}</div>
                <h3>{position.title}</h3>
                <ul className="career-position-meta">
                  <li>
                    <FaMapMarkerAlt /> {position.location}
                  </li>
                  <li>
                    <FaBriefcase /> {position.type}
                  </li>
                  <li>
                    <FaClock /> {position.duration}
                  </li>
                </ul>
                <p>{position.description}</p>
                <a
                  className="career-position-apply"
                  href={`mailto:info@nexite-it.com?subject=Bewerbung: ${encodeURIComponent(
                    position.title
                  )}`}
                >
                  Jetzt bewerben <FaArrowRight />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="career-cta reveal" ref={ctaRef}>
        <div className="career-cta-card">
          <div className="career-cta-text">
            <h2>Keine passende Stelle gefunden?</h2>
            <p>
              Wir freuen uns auch über Initiativbewerbungen – erzähl uns,
              wie du unser Team bereichern möchtest.
            </p>
          </div>
          <a
            href="mailto:info@nexite-it.com?subject=Initiativbewerbung"
            className="career-cta-button"
          >
            Initiativ bewerben <FaArrowRight />
          </a>
        </div>
      </section>
    </div>
  );
};

export default CareerPage;
